/*
  Renderer for the per-ticket bulk-booking response. Used inline below the
  validation panel once the operator presses Book selected. Returns null
  when nothing has run yet so the parent card can simply omit itself.
*/
import React from 'react';
import { Card, Col, Collapse, Empty, Row, Space, Statistic, Table, Tag, Typography } from 'antd';
import { CheckCircleTwoTone, CloseCircleTwoTone } from '@ant-design/icons';
import { isValidArray, isValidArrayWithValues, isValidString } from '../../utils/utilities';

const { Text } = Typography;
const { Panel } = Collapse;

export const renderBookingResults = (bookingResults) => {
    if (!bookingResults) {
        return null;
    }
    if (!isValidArrayWithValues(bookingResults.results)) {
        return <Empty description="No booking results to show." />;
    }
    return (
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
            <Row gutter={[16, 16]}>
                <Col xs={12} md={4}>
                    <Statistic title="Trips" value={bookingResults.summary.totalTrips} />
                </Col>
                <Col xs={12} md={4}>
                    <Statistic title="Tickets attempted" value={bookingResults.summary.totalTickets} />
                </Col>
                <Col xs={12} md={4}>
                    <Statistic title="Booked" value={bookingResults.summary.bookedTickets} valueStyle={{ color: '#52c41a' }} />
                </Col>
                <Col xs={12} md={4}>
                    <Statistic title="Failed" value={bookingResults.summary.failedTickets} valueStyle={{ color: '#ff4d4f' }} />
                </Col>
            </Row>
            <Collapse>
                {bookingResults.results.map((groupResult) => {
                    const succeeded = groupResult.tickets.filter((t) => t.success).length;
                    const failed = groupResult.tickets.length - succeeded;
                    const headerColor = groupResult.tripFound
                        ? (failed === 0 ? 'green' : 'orange')
                        : 'red';
                    const header = (
                        <Space>
                            {groupResult.tripFound
                                ? <CheckCircleTwoTone twoToneColor="#52c41a" />
                                : <CloseCircleTwoTone twoToneColor="#ff4d4f" />}
                            <Text strong>{groupResult.journeyDate}</Text>
                            <Text>{groupResult.serviceNumber || groupResult.route}</Text>
                            <Tag color={headerColor}>
                                {`${succeeded} booked / ${groupResult.tickets.length} attempted`}
                            </Tag>
                        </Space>
                    );
                    return (
                        <Panel header={header} key={`book-${groupResult.tripKey}`}>
                            {!groupResult.tripFound && (
                                <Text type="danger">
                                    {groupResult.tripIssue || 'Trip could not be matched.'}
                                </Text>
                            )}
                            {groupResult.tripFound && groupResult.trip && (
                                <Card size="small" style={{ marginBottom: 12 }}>
                                    <Row gutter={[16, 8]}>
                                        <Col span={8}><Text type="secondary">Trip ID:</Text> {groupResult.trip.tripId}</Col>
                                        <Col span={8}><Text type="secondary">Schedule:</Text> {groupResult.trip.scheduleDescription || groupResult.trip.scheduleId}</Col>
                                    </Row>
                                </Card>
                            )}
                            <Table
                                rowKey={(record) => `${groupResult.tripKey}-${record.pnr}`}
                                pagination={false}
                                size="small"
                                dataSource={groupResult.tickets}
                                columns={[
                                    { title: 'PNR (file)', dataIndex: 'pnr', key: 'pnr', width: 130 },
                                    {
                                        title: 'Result',
                                        key: 'result',
                                        width: 130,
                                        render: (_value, record) => (
                                            record.success
                                                ? <Tag color="green" icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}>Booked</Tag>
                                                : <Tag color="red" icon={<CloseCircleTwoTone twoToneColor="#ff4d4f" />}>Failed</Tag>
                                        ),
                                    },
                                    {
                                        title: 'New PNR',
                                        dataIndex: 'newPnr',
                                        key: 'newPnr',
                                        width: 130,
                                        render: (value) => (isValidString(value) ? <Text code>{value}</Text> : <Text type="secondary">—</Text>),
                                    },
                                    {
                                        title: 'Fare',
                                        key: 'fare',
                                        width: 90,
                                        render: (_v, r) => (r.finance && Number.isFinite(r.finance.fare) ? r.finance.fare.toFixed(2) : <Text type="secondary">—</Text>),
                                    },
                                    {
                                        title: 'CGST',
                                        key: 'cgst',
                                        width: 80,
                                        render: (_v, r) => (r.finance && Number.isFinite(r.finance.cgst) ? r.finance.cgst.toFixed(2) : <Text type="secondary">—</Text>),
                                    },
                                    {
                                        title: 'SGST',
                                        key: 'sgst',
                                        width: 80,
                                        render: (_v, r) => (r.finance && Number.isFinite(r.finance.sgst) ? r.finance.sgst.toFixed(2) : <Text type="secondary">—</Text>),
                                    },
                                    {
                                        title: 'Commission',
                                        key: 'commission',
                                        width: 100,
                                        render: (_v, r) => (r.finance && Number.isFinite(r.finance.commission) ? r.finance.commission.toFixed(2) : <Text type="secondary">—</Text>),
                                    },
                                    {
                                        title: 'Discount',
                                        key: 'discount',
                                        width: 90,
                                        render: (_v, r) => (r.finance && Number.isFinite(r.finance.discount) ? r.finance.discount.toFixed(2) : <Text type="secondary">—</Text>),
                                    },
                                    {
                                        title: 'Amount Paid',
                                        key: 'amountPaid',
                                        width: 110,
                                        render: (_v, r) => (r.finance && Number.isFinite(r.finance.amountPaid) ? r.finance.amountPaid.toFixed(2) : <Text type="secondary">—</Text>),
                                    },
                                    {
                                        title: 'Total',
                                        key: 'totalTicketCost',
                                        width: 100,
                                        render: (_v, r) => (r.finance && Number.isFinite(r.finance.totalTicketCost) ? r.finance.totalTicketCost.toFixed(2) : <Text type="secondary">—</Text>),
                                    },
                                    {
                                        title: 'Reason',
                                        dataIndex: 'reason',
                                        key: 'reason',
                                        render: (value) => value || '',
                                    },
                                ]}
                            />
                        </Panel>
                    );
                })}
            </Collapse>
        </Space>
    );
};

// Silence unused-import warning
void isValidArray;
